import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import './Reviews.css';

const Reviews = () => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/store-owner/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showError(t('reviews.errorLoading') || 'Error loading reviews');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="no-rating">{t('review.noRating')}</span>;
    return (
      <div className="star-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? 'filled' : ''}>★</span>
        ))}
        <span className="rating-value">({rating})</span>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-spinner">{t('reviews.loading')}</div>;
  }

  return (
    <div className="reviews-manager">
      <h2>{t('reviews.title')}</h2>

      <div className="reviews-stats">
        <div className="stat-card">
          <h3>{t('reviews.totalReviews')}</h3>
          <p className="stat-number">{reviews.length}</p>
        </div>
        <div className="stat-card">
          <h3>{t('reviews.avgDeliveryRating')}</h3>
          <p className="stat-number">
            {reviews.filter(r => r.deliveryRating).length > 0
              ? (reviews.reduce((sum, r) => sum + (r.deliveryRating || 0), 0) / reviews.filter(r => r.deliveryRating).length).toFixed(1)
              : 'N/A'}
          </p>
        </div>
        <div className="stat-card">
          <h3>{t('reviews.avgStoreRating')}</h3>
          <p className="stat-number">
            {reviews.filter(r => r.restaurantRating).length > 0
              ? (reviews.reduce((sum, r) => sum + (r.restaurantRating || 0), 0) / reviews.filter(r => r.restaurantRating).length).toFixed(1)
              : 'N/A'}
          </p>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="no-reviews">
          <p>{t('reviews.noReviews')}</p>
        </div>
      ) : (
        <div className="reviews-table-container">
          <table className="reviews-table">
            <thead>
              <tr>
                <th>{t('reviews.orderNumber')}</th>
                <th>{t('reviews.deliveryDriver')}</th>
                <th>{t('reviews.deliveryRating')}</th>
                <th>{t('reviews.deliveryComment')}</th>
                <th>{t('reviews.storeRating')}</th>
                <th>{t('reviews.restaurantComment')}</th>
                <th>{t('reviews.date')}</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review._id}>
                  <td>{review.order?.orderNumber || 'N/A'}</td>
                  <td>
                    {review.deliveryDriver
                      ? `${review.deliveryDriver.firstName} ${review.deliveryDriver.lastName}`
                      : 'N/A'}
                  </td>
                  <td>{renderStars(review.deliveryRating)}</td>
                  <td className="comment-cell">{review.deliveryComment || <span className="no-comment">{t('review.noComment')}</span>}</td>
                  <td>{renderStars(review.restaurantRating)}</td>
                  <td className="comment-cell">{review.restaurantComment || <span className="no-comment">{t('review.noComment')}</span>}</td>
                  <td>{formatDate(review.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Reviews;
